import { Box, Typography } from "@mui/material";
import {
  BarChart,
  GpsFixed,
  PieChart,
  VerifiedUser,
} from "@mui/icons-material";
import {
  getCoverFeatures,
  getCoverHeadline,
  getCoverScreens,
  getPostCategory,
  getPostCover,
  isBannerCover,
  screenshotWebp,
} from "../../utils/blogFormat";
import BrandMark from "../BrandMark";

const FEATURE_ICONS = {
  reports: PieChart,
  tracking: BarChart,
  secure: VerifiedUser,
  decisions: GpsFixed,
};

const Screenshot = ({ src, alt, priority }) => {
  const webp = screenshotWebp(src);
  return (
    <Box component="picture" sx={{ display: "block", width: "100%", height: "100%" }}>
      {webp && <source srcSet={webp} type="image/webp" />}
      <Box
        component="img"
        src={src}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "top center",
          display: "block",
        }}
      />
    </Box>
  );
};

const TabletMock = ({ src, alt, priority, rotate = "-12deg", zIndex = 2 }) => (
  <Box
    sx={{
      position: "absolute",
      inset: 0,
      zIndex,
      display: "flex",
      alignItems: "stretch",
      transform: `perspective(1400px) rotateY(${rotate}) rotateX(5deg)`,
      transformOrigin: "center bottom",
      filter: "drop-shadow(0 28px 36px rgba(69, 39, 160, 0.22))",
    }}
  >
    <Box
      sx={{
        width: "100%",
        bgcolor: "#F3F0F8",
        border: "1px solid #E4DFF0",
        borderRadius: { xs: "14px", md: "22px" },
        p: { xs: "5px", md: "8px" },
      }}
    >
      <Box
        sx={{
          height: "100%",
          bgcolor: "#111018",
          borderRadius: { xs: "10px", md: "16px" },
          overflow: "hidden",
        }}
      >
        <Screenshot src={src} alt={alt} priority={priority} />
      </Box>
    </Box>
  </Box>
);

const TreasureLogo = ({ compact }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: compact ? 0.7 : 1, mb: compact ? 0.75 : 1.5 }}>
    <BrandMark size={compact ? 26 : 36} />
    <Typography
      sx={{
        fontWeight: 800,
        color: "#16161D",
        fontSize: { xs: "0.72rem", md: compact ? "0.95rem" : "1.15rem" },
        letterSpacing: "-0.03em",
        lineHeight: 1,
      }}
    >
      Treasure
    </Typography>
  </Box>
);

const CoinsAndPlant = () => (
  <Box
    aria-hidden
    sx={{
      position: "absolute",
      left: { xs: 10, md: 28 },
      bottom: { xs: 8, md: 14 },
      display: "flex",
      alignItems: "flex-end",
      gap: 1,
      zIndex: 2,
    }}
  >
    <svg width="34" height="42" viewBox="0 0 34 42" fill="none">
      <rect x="10" y="28" width="14" height="10" rx="2" fill="#F5F3FA" stroke="#E0D7F0" />
      <path d="M17 28c-6-9-1-16 0-20 4 6 8 11 4 20" fill="#43A047" />
      <path d="M17 28c6-8 2-15 1-19-5 7-7 12-1 19" fill="#66BB6A" />
    </svg>
    <svg width="42" height="32" viewBox="0 0 42 32" fill="none">
      <ellipse cx="21" cy="26" rx="14" ry="4.5" fill="#F0C14B" />
      <ellipse cx="21" cy="21" rx="14" ry="4.5" fill="#FFD54F" />
      <ellipse cx="21" cy="16" rx="14" ry="4.5" fill="#FFC107" />
      <text x="21" y="18.5" textAnchor="middle" fontSize="8" fontWeight="800" fill="#8D6E00">
        ₹
      </text>
    </svg>
  </Box>
);

/**
 * 16:9 Treasure marketing cover.
 * Default: compose logo, headline, feature icons, and two tablet mockups from post screenshots.
 * Future banners: set cover to a file in /public/covers/ to show that 16:9 image as-is.
 */
const BlogCover = ({
  post,
  src,
  alt,
  size = "card",
  priority = false,
  titleComponent = "p",
}) => {
  const image = src || getPostCover(post);
  const label = alt || post?.title || "";
  const isHero = size === "hero";
  const isFeatured = size === "featured";
  const compact = size === "thumb" || size === "card";
  const roomy = isHero || isFeatured;

  if (isBannerCover(image)) {
    return (
      <Box sx={{ position: "relative", aspectRatio: "16 / 9", overflow: "hidden", bgcolor: "#FBF9FE" }}>
        <Box
          component="img"
          src={image}
          alt={label}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </Box>
    );
  }

  const { lead, accent } = getCoverHeadline(post);
  const { primary, secondary } = getCoverScreens(post);
  const features = getCoverFeatures(post);
  const category = getPostCategory(post);
  const dek = post?.description || "";
  const tagline = post?.tagline || "Your Money, Your Rules";
  const taglineBody = post?.taglineBody || "Manage every transaction and build better financial habits.";

  return (
    <Box
      sx={{
        position: "relative",
        aspectRatio: "16 / 9",
        overflow: "hidden",
        bgcolor: "#FBF9FE",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          width: "42%",
          height: "48%",
          opacity: 0.55,
          backgroundImage: "radial-gradient(#D1C4E9 1.15px, transparent 1.15px)",
          backgroundSize: "16px 16px",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: "-8%",
          bottom: "-18%",
          width: "70%",
          height: "42%",
          borderRadius: "50%",
          background: "radial-gradient(ellipse at center, rgba(126,87,194,0.16), transparent 68%)",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          right: "18%",
          top: "12%",
          color: "#D1C4E9",
          fontWeight: 300,
          fontSize: { xs: "0.9rem", md: "1.4rem" },
        }}
      >
        +
      </Box>

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          height: "100%",
          display: "grid",
          gridTemplateColumns: "1.08fr 0.92fr",
          px: { xs: 1.25, sm: 2, md: isHero ? 4.5 : 3 },
          py: { xs: 1.1, sm: 1.5, md: isHero ? 3 : 2.25 },
          gap: { xs: 0.5, md: 1 },
        }}
      >
        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <TreasureLogo compact={compact} />
          <Typography
            component={titleComponent}
            sx={{
              m: 0,
              fontWeight: 800,
              color: "#16161D",
              letterSpacing: "-0.04em",
              lineHeight: 1.12,
              fontSize: {
                xs: compact ? "0.72rem" : "0.95rem",
                sm: isFeatured || isHero ? "1.45rem" : "1.05rem",
                md: isHero ? "2.35rem" : isFeatured ? "1.85rem" : "1.15rem",
              },
              display: "-webkit-box",
              WebkitLineClamp: compact ? 3 : 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {lead}
            {accent ? " " : ""}
            {accent ? (
              <Box component="span" sx={{ color: "primary.main" }}>
                {accent}
              </Box>
            ) : null}
          </Typography>
          {dek && (
            <Typography
              sx={{
                mt: { xs: 0.5, md: 1.1 },
                color: "#5C5670",
                fontWeight: 500,
                lineHeight: 1.45,
                fontSize: {
                  xs: compact ? "0.48rem" : "0.62rem",
                  md: isHero ? "0.98rem" : compact ? "0.68rem" : "0.82rem",
                },
                display: "-webkit-box",
                WebkitLineClamp: compact ? 2 : 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                maxWidth: "36em",
              }}
            >
              {dek}
            </Typography>
          )}

          <Box
            sx={{
              mt: { xs: 0.75, md: 1.75 },
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: { xs: 0.45, md: 1.1 },
              maxWidth: { md: 420 },
            }}
          >
            {features.map((item) => {
              const Icon = FEATURE_ICONS[item.key] || PieChart;
              return (
                <Box key={item.key} sx={{ display: "flex", alignItems: "center", gap: { xs: 0.45, md: 0.9 } }}>
                  <Box
                    sx={{
                      width: { xs: 16, md: roomy ? 40 : 28 },
                      height: { xs: 16, md: roomy ? 40 : 28 },
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: { xs: 10, md: roomy ? 20 : 15 } }} />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: "#2A2438",
                      fontSize: { xs: "0.42rem", md: compact ? "0.62rem" : "0.78rem" },
                      lineHeight: 1.2,
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {roomy && (
            <Box
              sx={{
                mt: { xs: 1, md: 2.25 },
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                bgcolor: "primary.main",
                color: "white",
                borderRadius: 2,
                px: { xs: 1, md: 1.75 },
                py: { xs: 0.7, md: 1.15 },
                maxWidth: 430,
              }}
            >
              <Box
                sx={{
                  width: { xs: 22, md: 32 },
                  height: { xs: 22, md: 32 },
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.16)",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <VerifiedUser sx={{ fontSize: { xs: 14, md: 18 } }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: { xs: "0.62rem", md: "0.88rem" }, lineHeight: 1.2 }}>
                  {tagline}
                </Typography>
                <Typography sx={{ opacity: 0.92, fontSize: { xs: "0.48rem", md: "0.72rem" }, lineHeight: 1.35 }}>
                  {taglineBody}
                </Typography>
              </Box>
            </Box>
          )}

          {!roomy && (
            <Typography
              sx={{
                mt: 0.7,
                color: "primary.main",
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontSize: { xs: "0.4rem", md: "0.58rem" },
              }}
            >
              {category}
            </Typography>
          )}
        </Box>

        <Box sx={{ position: "relative", minWidth: 0, height: "100%" }}>
          <Box sx={{ position: "absolute", left: "2%", top: "6%", bottom: "4%", width: "58%" }}>
            <TabletMock
              src={primary}
              alt={label}
              priority={priority}
              rotate="-16deg"
              zIndex={2}
            />
          </Box>
          <Box sx={{ position: "absolute", right: "-2%", top: "14%", bottom: 0, width: "62%" }}>
            <TabletMock
              src={secondary}
              alt=""
              priority={priority}
              rotate="14deg"
              zIndex={3}
            />
          </Box>
        </Box>
      </Box>

      {roomy && <CoinsAndPlant />}
    </Box>
  );
};

export default BlogCover;
